import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API, useAuth } from '@/App';
import { resolveImageUrl } from '@/utils/imageUrl';
import axios from 'axios';
import { Heart, Send, CornerDownRight, ChevronDown, ChevronUp, Trash2, Pencil, Check, X } from 'lucide-react';
import { toast } from 'sonner';

function formatTime(isoStr) {
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

function SingleComment({ comment, onReply, onLike, onDelete, onEdit, userId, postOwnerId }) {
  const navigate = useNavigate();
  const isLiked = comment.likes?.includes(userId);
  const isOwner = comment.user_id === userId;
  const isPostOwner = postOwnerId === userId;
  const canDelete = isOwner || isPostOwner;
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(comment.content);

  const handleSaveEdit = () => {
    if (editText.trim() && editText !== comment.content) {
      onEdit(comment.comment_id, editText.trim());
    }
    setEditing(false);
  };

  return (
    <div className="flex gap-2" data-testid={`comment-${comment.comment_id}`}>
      <div className="w-7 h-7 rounded-full bg-blue-50 flex items-center justify-center shrink-0 overflow-hidden mt-0.5 cursor-pointer"
        onClick={() => comment.user_id && navigate(`/user/${comment.user_id}`)}>
        {comment.user_avatar ? (
          <img src={resolveImageUrl(comment.user_avatar)} alt="" className="w-full h-full object-cover" />
        ) : (
          <span className="text-[10px] font-bold text-[#2F80ED]">{comment.user_name?.[0]?.toUpperCase()}</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="bg-slate-50 rounded-2xl px-3 py-2">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-semibold text-[#0A2540] cursor-pointer hover:text-[#2F80ED] transition-colors"
              onClick={() => comment.user_id && navigate(`/user/${comment.user_id}`)}>{comment.user_name}</span>
            {/* Verified / system badges */}
            {comment.is_ai && (
              <span className="px-1.5 py-0.5 rounded-full bg-teal-100 text-teal-700 text-[9px] font-bold uppercase tracking-wider">✦ Unexa AI</span>
            )}
            {!comment.is_ai && comment.user_role === 'agent' && (
              <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold uppercase tracking-wider">✅ MARA Verified</span>
            )}
            {!comment.is_ai && comment.user_role === 'institution' && (
              <span className="px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-[9px] font-bold uppercase tracking-wider">🏫 Verified Institution</span>
            )}
          </div>
          {editing ? (
            <div className="flex items-center gap-1 mt-0.5">
              <input value={editText} onChange={(e) => setEditText(e.target.value)}
                className="flex-1 text-comment bg-white border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:border-[#2F80ED]"
                data-testid={`edit-comment-input-${comment.comment_id}`} autoFocus />
              <button onClick={handleSaveEdit} className="text-emerald-500 hover:text-emerald-600"><Check className="h-3.5 w-3.5" /></button>
              <button onClick={() => setEditing(false)} className="text-slate-400 hover:text-slate-600"><X className="h-3.5 w-3.5" /></button>
            </div>
          ) : (
            <p className="text-comment text-[#0A2540] leading-relaxed mt-0.5">
              {comment.content}
              {comment.edited && <span className="text-caption text-[#65676B] ml-1">(edited)</span>}
            </p>
          )}
          {/* Role-specific action button */}
          {comment.is_ai && comment.ai_action_link && (
            <button
              onClick={() => navigate(comment.ai_action_link)}
              className="mt-2 px-3 py-1.5 rounded-full bg-teal-600 hover:bg-teal-700 text-white text-[11px] font-bold transition-colors"
              data-testid={`ai-action-${comment.comment_id}`}
            >
              {comment.ai_action_label || 'Find a MARA Agent'} →
            </button>
          )}
          {!comment.is_ai && comment.user_role === 'agent' && comment.user_id && (
            <button
              onClick={() => navigate(`/agent/${comment.user_id}`)}
              className="mt-2 px-3 py-1.5 rounded-full bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-[11px] font-bold transition-colors"
              data-testid={`agent-cta-${comment.comment_id}`}
            >
              Book Free Consultation →
            </button>
          )}
          {!comment.is_ai && comment.user_role === 'institution' && comment.user_id && (
            <button
              onClick={() => navigate(`/college/${comment.user_id}`)}
              className="mt-2 px-3 py-1.5 rounded-full bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-[11px] font-bold transition-colors"
              data-testid={`inst-cta-${comment.comment_id}`}
            >
              View Course →
            </button>
          )}
        </div>
        <div className="flex items-center gap-3 mt-1 ml-1">
          <button onClick={() => onLike(comment.comment_id)}
            className={`text-caption font-medium transition-colors ${isLiked ? 'text-red-500' : 'text-[#65676B] hover:text-[#0A2540]'}`}>
            {comment.like_count > 0 ? `${comment.like_count} ` : ''}Like
          </button>
          <button onClick={() => onReply(comment)}
            className="text-caption font-medium text-[#65676B] hover:text-[#2F80ED] transition-colors">Reply</button>
          {isOwner && (
              <button onClick={() => { setEditing(true); setEditText(comment.content); }}
                className="text-caption font-medium text-[#65676B] hover:text-[#2F80ED] transition-colors flex items-center gap-0.5"
                data-testid={`edit-comment-${comment.comment_id}`}>
                <Pencil className="h-2.5 w-2.5" /> Edit
              </button>
          )}
          {canDelete && (
              <button onClick={() => onDelete(comment.comment_id)}
                className="text-caption font-medium text-[#65676B] hover:text-red-600 transition-colors flex items-center gap-0.5"
                data-testid={`delete-comment-${comment.comment_id}`}>
                <Trash2 className="h-2.5 w-2.5" /> Delete
              </button>
          )}
          <span className="text-caption text-[#65676B]">{formatTime(comment.created_at)}</span>
        </div>
      </div>
    </div>
  );
}

export function CommentSection({ postId, commentCount = 0, isOpen, onToggle, postOwnerId }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const expanded = isOpen !== undefined ? isOpen : false;
  const setExpanded = onToggle || (() => {});
  const [newComment, setNewComment] = useState('');
  const [sending, setSending] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [expandedReplies, setExpandedReplies] = useState({});

  const fetchComments = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/posts/${postId}/comments`);
      setComments(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (expanded) fetchComments();
  }, [expanded]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!newComment.trim() || !user) return;
    setSending(true);
    try {
      await axios.post(`${API}/posts/${postId}/comments`, {
        content: newComment,
        parent_id: replyTo?.comment_id || null,
      });
      setNewComment('');
      setReplyTo(null);
      fetchComments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to comment');
    } finally {
      setSending(false);
    }
  };

  const handleLikeComment = async (commentId) => {
    if (!user) return;
    try {
      await axios.post(`${API}/comments/${commentId}/like`, {});
      fetchComments();
    } catch (e) { /* silent */ }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axios.delete(`${API}/comments/${commentId}`);
      toast.success('Comment deleted');
      fetchComments();
    } catch { toast.error('Failed to delete comment'); }
  };

  const handleEditComment = async (commentId, newContent) => {
    try {
      await axios.put(`${API}/comments/${commentId}`, { content: newContent });
      toast.success('Comment updated');
      fetchComments();
    } catch { toast.error('Failed to edit comment'); }
  };

  const toggleReplies = (commentId) => {
    setExpandedReplies(prev => ({ ...prev, [commentId]: !prev[commentId] }));
  };

  return (
    <div data-testid={`comments-section-${postId}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-[#2F80ED] transition-colors px-4 pb-2"
        data-testid={`toggle-comments-${postId}`}
      >
        {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        {expanded ? 'Hide' : 'View'} {commentCount} comments
      </button>

      {expanded && (
        <div className="px-4 pb-3 animate-slide-up">
          {/* Comment Input */}
          {user ? (
            <form onSubmit={handleSubmit} className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-full bg-blue-50 flex items-center justify-center shrink-0 overflow-hidden">
                {user.profile_picture ? (
                  <img src={user.profile_picture} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-[10px] font-bold text-[#2F80ED]">{user.name?.[0]?.toUpperCase()}</span>
                )}
              </div>
              <div className="flex-1">
                {replyTo && (
                  <div className="flex items-center gap-1 text-[10px] text-[#2F80ED] mb-0.5">
                    <CornerDownRight className="h-2.5 w-2.5" />
                    Replying to {replyTo.user_name}
                    <button onClick={() => setReplyTo(null)} className="text-slate-400 hover:text-slate-600 ml-1">x</button>
                  </div>
                )}
                <input
                  type="text"
                  placeholder={replyTo ? `Reply to ${replyTo.user_name}...` : 'Write a comment...'}
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className="w-full h-8 px-3 rounded-full bg-slate-100 border border-slate-200 text-xs placeholder:text-slate-300 focus:outline-none focus:border-[#2F80ED] text-slate-700"
                  data-testid={`comment-input-${postId}`}
                />
              </div>
              <button
                type="submit"
                disabled={!newComment.trim() || sending}
                className="w-8 h-8 rounded-full bg-[#2F80ED] flex items-center justify-center text-white shrink-0 disabled:opacity-40 active:scale-90 transition-all"
                data-testid={`comment-send-${postId}`}
              >
                <Send className="h-3.5 w-3.5" />
              </button>
            </form>
          ) : (
            <p className="text-xs text-slate-400 mb-3 text-center">Sign in to comment</p>
          )}

          {/* Comments List */}
          {loading ? (
            <div className="space-y-2">
              <div className="h-10 shimmer rounded-lg" />
              <div className="h-10 shimmer rounded-lg" />
            </div>
          ) : comments.length === 0 ? (
            <p className="text-xs text-slate-300 text-center py-2">No comments yet. Be the first!</p>
          ) : (
            <div className="space-y-3">
              {comments.map(comment => (
                <div key={comment.comment_id}>
                  <SingleComment comment={comment} onReply={setReplyTo} onLike={handleLikeComment} onDelete={handleDeleteComment} onEdit={handleEditComment} userId={user?.user_id} postOwnerId={postOwnerId} />
                  {/* Threaded Replies */}
                  {comment.replies?.length > 0 && (
                    <div className="ml-9 mt-1">
                      {!expandedReplies[comment.comment_id] ? (
                        <button
                          onClick={() => toggleReplies(comment.comment_id)}
                          className="flex items-center gap-1 text-[10px] text-[#2F80ED] font-medium hover:text-[#0A2540] transition-colors"
                        >
                          <CornerDownRight className="h-3 w-3" />
                          {comment.replies.length} {comment.replies.length === 1 ? 'reply' : 'replies'}
                        </button>
                      ) : (
                        <div className="space-y-2 mt-1">
                          {comment.replies.map(reply => (
                            <SingleComment key={reply.comment_id} comment={reply} onReply={setReplyTo} onLike={handleLikeComment} onDelete={handleDeleteComment} onEdit={handleEditComment} userId={user?.user_id} postOwnerId={postOwnerId} />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
